import angular from 'angular';
import { LoadParams } from '../../../shared/models/loadparams.model';
import { IExclusaoLoteResultado, IGerenciamentoDocumento } from './models/gerenciamentodocumento.model';

export const GERENCIAMENTO_DOCUMENTOS_PAGE_LIMIT = 500;

export interface IGerenciamentoDocumentosLoadParams {
    status?: string;
    tipoarquivo?: string;
    trabalhador?: string;
    estabelecimento?: string;
    competenciaano?: string | number;
    competenciames?: string | number;
    campos_customizados?: Record<string, any> | string;
    'trabalhador.empresa'?: string;
    'trabalhador.estabelecimento'?: string;
    'trabalhador.lotacao'?: string;
    grupo?: string;
    order?: Record<string, any> | string;
    offset?: number | string | Record<string, any>;
    limit?: number;
}

export interface IGerenciamentoDocumentosLoadResult {
    data: IGerenciamentoDocumento[];
    meta?: Record<string, any>;
}

type LoadOptions = {
    silent?: boolean;
    append?: boolean;
};

export class GerenciamentoDocumentosService {
    static $inject = ['$http', 'nsjRouting', '$rootScope', '$q'];

    public entities: IGerenciamentoDocumento[] = [];
    public loaded = false;
    public constructors: IGerenciamentoDocumentosLoadParams = {};
    public filters: IGerenciamentoDocumentosLoadParams = {};
    public filter: string = '';
    public order?: any;
    public loadParams: LoadParams;
    public meta: Record<string, any> | null = null;
    public pagination = {
        currentPage: 1,
        pageSize: GERENCIAMENTO_DOCUMENTOS_PAGE_LIMIT,
        totalItems: 0
    };
    public loading_deferred: angular.IDeferred<unknown> | null = null;
    public lastOffset: string | number | null = null;

    constructor(
        public $http: angular.IHttpService,
        public nsjRouting: any,
        public $rootScope: angular.IRootScopeService,
        public $q: angular.IQService,
    ) {
        this.loadParams = new LoadParams();
        this.loadParams.to_load = 1;
    }

    public setConstructors(constructors: IGerenciamentoDocumentosLoadParams = {}): void {
        this.constructors = angular.copy(constructors || {});
    }

    public setFilters(filters: IGerenciamentoDocumentosLoadParams = {}): void {
        this.filters = angular.copy(filters || {});
    }

    public setFilterTerm(filter: string): void {
        this.filter = filter;
    }

    public setOrder(order?: IGerenciamentoDocumentosLoadParams['order']): void {
        this.order = order;
    }

    public setPageSize(size: number): void {
        if (Number.isFinite(size) && size > 0) {
            this.pagination.pageSize = Math.floor(size);
        }
    }

    public resetState(): void {
        if (this.loading_deferred) {
            this.loading_deferred.resolve();
            this.loading_deferred = null;
        }

        this.entities.length = 0;
        this.loaded = false;
        this.constructors = {};
        this.filters = {};
        this.filter = '';
        this.order = undefined;
        this.meta = null;
        this.pagination = {
            currentPage: 1,
            pageSize: GERENCIAMENTO_DOCUMENTOS_PAGE_LIMIT,
            totalItems: 0
        };
        this.loadParams = new LoadParams();
        this.loadParams.to_load = 1;
        this.lastOffset = null;
    }

    public reload(): angular.IPromise<IGerenciamentoDocumento[]> {
        this.pagination.currentPage = 1;
        this.lastOffset = null;
        return this.load(1);
    }

    public goToPage(page: number): angular.IPromise<IGerenciamentoDocumento[]> {
        return this.load(page);
    }

    public load(page: number = this.pagination.currentPage, options: LoadOptions = {}): angular.IPromise<IGerenciamentoDocumento[]> {
        const targetPage = page && page > 0 ? Math.floor(page) : 1;
        const { silent = false, append = false } = options;
        const hasValidOffset = this.lastOffset !== null && this.lastOffset !== undefined && this.lastOffset !== '';

        if (append && (this.loadParams.finished || !hasValidOffset)) {
            if (!this.loadParams.finished && !hasValidOffset) {
                this.loadParams.finished = true;
            }
            return this.$q.resolve(this.entities);
        }

        this.pagination.currentPage = targetPage;

        if (this.loading_deferred) {
            this.loading_deferred.resolve();
        }

        this.loading_deferred = this.$q.defer();
        this.loadParams.busy = true;
        this.loadParams.finished = false;

        if (!silent && !append) {
            this.$rootScope.$broadcast('gerenciamentodocumentos_list_loading');
        }

        const offsetParam = append || targetPage > 1 ? this.lastOffset : undefined;

        this.order = offsetParam && null;

        const queryParams = this.buildQueryParams({
            limit: this.pagination.pageSize,
            ...this.constructors,
            ...this.filters,
            trabalhador: this.filter?.trim() || undefined,
            offset: offsetParam === null || offsetParam === undefined || offsetParam === '' ? undefined : offsetParam,
            order: this.order,
            limit: GERENCIAMENTO_DOCUMENTOS_PAGE_LIMIT,
        });

        const requestConfig: angular.IRequestShortcutConfig = {
            timeout: this.loading_deferred.promise
        };

        return this.$http
            .get<any>(this.nsjRouting.generate('meurh_arquivostrabalhadores_index', queryParams, true), requestConfig)
            .then((response) => this.handleLoadSuccess(response, { silent, append }))
            .catch((error) => this.handleLoadError(error))
            .finally(() => {
                this.loading_deferred = null;
                this.loadParams.busy = false;
                this.loadParams.to_load = 0;
            });
    }

    public loadNextPage(): angular.IPromise<IGerenciamentoDocumento[]> {
        if (this.loadParams.busy || this.loadParams.finished) {
            return this.$q.resolve(this.entities);
        }

        const nextPage = this.pagination.currentPage + 1;
        return this.load(nextPage, { append: true });
    }

    public getPendencias(params: IGerenciamentoDocumentosLoadParams = {}): angular.IPromise<any> {
        const queryParams = this.buildQueryParams(params);

        return this.$http
            .get<any>(this.nsjRouting.generate('meurh_arquivostrabalhadores_pendencias', queryParams, true))
            .then((response) => response.data)
            .catch((error) => {
                console.error('Erro ao buscar pendencias dos documentos:', error);
                throw error;
            });
    }

    public getHistorico(arquivoTrabalhadorId: string): angular.IPromise<any[]> {
        if (!arquivoTrabalhadorId) {
            return this.$q.reject('ID do arquivo do trabalhador é obrigatório');
        }

        const params = {
            arquivotrabalhador: arquivoTrabalhadorId
        };

        return this.$http
            .get<any>(this.nsjRouting.generate('meurh_arquivostrabalhadoreshistoricos_get', params, true))
            .then((response) => {
                return response.data || [];
            })
            .catch((error) => {
                console.error('Erro ao buscar histórico do documento:', error);
                throw error;
            });
    }

    public getArquivo(arquivoTrabalhadorId: string): angular.IPromise<angular.IHttpResponse<File>> {
        if (!arquivoTrabalhadorId) {
            return this.$q.reject('ID do arquivo do trabalhador é obrigatório');
        }

        const params = {
            id: arquivoTrabalhadorId
        };

        return this.$http
            .get<File>(this.nsjRouting.generate('meurh_arquivostrabalhadores_get_arquivo', params, true), {
                responseType: 'blob'
            })
            .then((response) => {
                this.$rootScope.$broadcast('meurh_arquivostrabalhadores_get_arquivo_loaded', response);
                return response;
            })
            .catch((error) => {
                console.error('Erro ao baixar documento:', error);
                throw error;
            });
    }

    public getConteudo(arquivoTrabalhadorId: string): angular.IPromise<angular.IHttpResponse<File>> {
        return this.getArquivo(arquivoTrabalhadorId);
    }

    public liberarDocumento(arquivoTrabalhadorId: string): angular.IPromise<any> {
        if (!arquivoTrabalhadorId) {
            return this.$q.reject('ID do arquivo do trabalhador é obrigatório');
        }

        const params = {
            id: arquivoTrabalhadorId
        };

        return this.$http
            .put<any>(this.nsjRouting.generate('meurh_arquivostrabalhadores_liberar', params, true), {})
            .then((response) => {
                this.$rootScope.$broadcast('meurh_arquivostrabalhadores_liberar_loaded', response);
                return response.data;
            })
            .catch((error) => {
                console.error('Erro ao liberar documento:', error);
                throw error;
            });
    }

    public desfazerLiberacaoDocumento(arquivoTrabalhadorId: string): angular.IPromise<any> {
        if (!arquivoTrabalhadorId) {
            return this.$q.reject('ID do arquivo do trabalhador é obrigatório');
        }

        const params = {
            id: arquivoTrabalhadorId
        };

        return this.$http
            .post<any>(this.nsjRouting.generate('meurh_arquivostrabalhadores_desfazer_liberar', params, true), {})
            .then((response) => {
                this.$rootScope.$broadcast('meurh_arquivostrabalhadores_desfazer_liberar_loaded', response);
                return response.data;
            })
            .catch((error) => {
                console.error('Erro ao desfazer liberação do documento:', error);
                throw error;
            });
    }

    public reenviarDocumento(arquivoTrabalhadorId: string): angular.IPromise<any> {
        this.loading_deferred = this.$q.defer();
        if (!arquivoTrabalhadorId) {
            return this.$q.reject('ID do arquivo do trabalhador é obrigatório');
        }

        const params = {
            id: arquivoTrabalhadorId
        };

        return this.$q((resolve, reject) => {
            this.$http.post<void>(
                this.nsjRouting.generate('meurh_arquivostrabalhadores_reenviar', {
                    id: arquivoTrabalhadorId
                }, true),
                { timeout: this.loading_deferred?.promise }
            )
            .then((response) => {
                this.$rootScope.$broadcast('meurh_arquivostrabalhadores_reenviar_loaded', response.data);
                resolve(response.data);
            })
            .catch((response) => {
                reject(response);
            });
            });
    }

    public cancelarAssinatura(arquivoTrabalhadorId: string, comentario: string): angular.IPromise<any> {
        if (!arquivoTrabalhadorId) {
            return this.$q.reject('ID do arquivo do trabalhador é obrigatório');
        }

        if (!comentario || !comentario.trim()) {
            return this.$q.reject('Comentário do cancelamento é obrigatório.');
        }

        const params = {
            id: arquivoTrabalhadorId
        };

        const payload = {
            comentario: comentario.trim()
        };

        return this.$http
            .post<any>(this.nsjRouting.generate('meurh_arquivostrabalhadores_cancelar', params, true), payload)
            .then((response) => {
                this.$rootScope.$broadcast('meurh_arquivostrabalhadores_cancelar_assinatura_loaded', response);
                return response.data;
            })
            .catch((error) => {
                console.error('Erro ao cancelar documento:', error);
                throw error;
            });
    }

    public reenviarDocumentosLote(arquivosTrabalhadoresIds: string[]): angular.IPromise<any> {
        if (!Array.isArray(arquivosTrabalhadoresIds) || !arquivosTrabalhadoresIds.length) {
            return this.$q.reject('É necessário informar pelo menos um documento para reenviar.');
        }

        const payload = {
            documentos: arquivosTrabalhadoresIds
        };

        return this.$http
            .post<any>(this.nsjRouting.generate('meurh_arquivostrabalhadores_reenviar_lote', {}, true), payload)
            .then((response) => {
                this.$rootScope.$broadcast('meurh_arquivostrabalhadores_reenviar_lote_loaded', response);
                return response.data;
            })
            .catch((error) => {
                console.error('Erro ao reenviar documentos em lote:', error);
                throw error;
            });
    }

    public liberarDocumentosLote(arquivosTrabalhadoresIds: string[]): angular.IPromise<any> {
        if (!Array.isArray(arquivosTrabalhadoresIds) || !arquivosTrabalhadoresIds.length) {
            return this.$q.reject('É necessário informar pelo menos um documento para liberação.');
        }

        const payload = {
            documentos: arquivosTrabalhadoresIds
        };

        return this.$http
            .post<any>(this.nsjRouting.generate('meurh_arquivostrabalhadores_liberar_lote', {}, true), payload)
            .then((response) => {
                this.$rootScope.$broadcast('meurh_arquivostrabalhadores_liberar_lote_loaded', response);
                return response.data;
            })
            .catch((error) => {
                console.error('Erro ao liberar documentos em lote:', error);
                throw error;
            });
    }

    public desfazerLiberacaoDocumentosLote(arquivosTrabalhadoresIds: string[]): angular.IPromise<any> {
        if (!Array.isArray(arquivosTrabalhadoresIds) || !arquivosTrabalhadoresIds.length) {
            return this.$q.reject('É necessário informar pelo menos um documento para desfazer a liberação.');
        }

        const payload = {
            documentos: arquivosTrabalhadoresIds
        };

        return this.$http
            .post<any>(this.nsjRouting.generate('meurh_arquivostrabalhadores_desfazer_liberar_lote', {}, true), payload)
            .then((response) => {
                this.$rootScope.$broadcast('meurh_arquivostrabalhadores_desfazer_liberar_lote_loaded', response);
                return response.data;
            })
            .catch((error) => {
                console.error('Erro ao desfazer liberação dos documentos:', error);
                throw error;
            });
    }

    public excluirDocumento(arquivoTrabalhadorId: string): angular.IPromise<any> {
        if (!arquivoTrabalhadorId) {
            return this.$q.reject('ID do arquivo do trabalhador é obrigatório');
        }

        const params = {
            id: arquivoTrabalhadorId
        };

        return this.$http
            .delete<any>(this.nsjRouting.generate('meurh_arquivostrabalhadores_delete', params, true))
            .then((response) => {
                this.$rootScope.$broadcast('meurh_arquivostrabalhadores_excluir_loaded', response);
                return response.data;
            })
            .catch((error) => {
                console.error('Erro ao excluir documento:', error);
                throw error;
            });
    }

    public excluirDocumentosLote(arquivosTrabalhadoresIds: string[]): angular.IPromise<IExclusaoLoteResultado> {
        if (!Array.isArray(arquivosTrabalhadoresIds) || !arquivosTrabalhadoresIds.length) {
            return this.$q.reject('É necessário informar pelo menos um documento para exclusão.');
        }

        const payload = {
            documentos: arquivosTrabalhadoresIds
        };

        return this.$http
            .post<IExclusaoLoteResultado>(this.nsjRouting.generate('meurh_arquivostrabalhadores_excluir_lote', {}, true), payload)
            .then((response) => {
                this.$rootScope.$broadcast('meurh_arquivostrabalhadores_excluir_lote_loaded', response);
                return response.data;
            })
            .catch((error) => {
                console.error('Erro ao excluir documentos em lote:', error);
                throw error;
            });
    }

    public handleLoadSuccess(
        response: angular.IHttpResponse<any>,
        options: LoadOptions
    ): IGerenciamentoDocumento[] {
        const { silent = false, append = false } = options;
        const payload = response?.data;
        const data = this.extractData(payload);
        const meta = this.extractMeta(payload, response.headers);
        const resolvedPageSize = GERENCIAMENTO_DOCUMENTOS_PAGE_LIMIT;
        const pageData = data.slice(0, resolvedPageSize);

        if (!append) {
            this.entities.length = 0;
        }

        pageData.forEach((item) => {
            this.entities.push(item);
        });

        this.meta = meta;
        this.pagination.pageSize = resolvedPageSize;
        const offsetIndex = this.pagination.currentPage - 1;
        const currentOffsetBase = offsetIndex * resolvedPageSize;
        this.pagination.totalItems = this.resolveTotalItems(meta, pageData.length, currentOffsetBase, resolvedPageSize);

        const hasNext = this.resolveHasNext(pageData.length, resolvedPageSize);

        this.loadParams.finished = !hasNext;
        this.loaded = true;

        if (pageData.length) {
            const lastEntity = pageData[pageData.length - 1];
            this.lastOffset = this.resolveOffsetValue(lastEntity);
        } else if (!append) {
            this.lastOffset = null;
        }

        if (!silent) {
            this.$rootScope.$broadcast('gerenciamentodocumentos_list_finished', {
                entities: this.entities,
                meta: this.meta,
                pagination: { ...this.pagination },
                hasNext
            });
        }

        return this.entities;
    }

    public handleLoadError(error: any): IGerenciamentoDocumento[] {
        if (error && error.xhrStatus === 'abort') {
            return this.entities;
        }

        this.entities.length = 0;
        this.meta = null;
        this.pagination.totalItems = 0;
        this.loadParams.finished = true;
        this.loaded = false;
        this.lastOffset = null;

        this.$rootScope.$broadcast('gerenciamentodocumentos_list_error', error);

        throw error;
    }

    public extractData(payload: any | undefined): IGerenciamentoDocumento[] {
        if (!payload) {
            return [];
        }

        if (Array.isArray(payload)) {
            return payload as IGerenciamentoDocumento[];
        }

        if (Array.isArray(payload.data)) {
            return payload.data as IGerenciamentoDocumento[];
        }

        return [];
    }

    public extractMeta(
        payload: any,
        headers: angular.IHttpHeadersGetter
    ): Record<string, any> | null {
        if (payload && !Array.isArray(payload) && payload.meta && typeof payload.meta === 'object') {
            return payload.meta;
        }

        if (payload && !Array.isArray(payload) && payload?.pagination && typeof payload.pagination === 'object') {
            return payload.pagination;
        }

        const totalHeader = headers?.('x-total-count');
        if (totalHeader) {
            const total = Number(totalHeader);
            if (!Number.isNaN(total)) {
                return { total };
            }
        }

        return null;
    }

    public resolveTotalItems(
        meta: Record<string, any> | null,
        currentLength: number,
        offset: number,
        pageSize: number
    ): number {
        const candidateKeys = ['total', 'totalItems', 'total_items', 'count', 'totalCount', 'total_count', 'recordsTotal'];
        for (const key of candidateKeys) {
            const value = meta?.[key];
            if (typeof value === 'number') {
                return value;
            }
        }

        if (meta && typeof meta.total_pages === 'number') {
            return meta.total_pages * pageSize;
        }

        if (meta && typeof meta.totalPages === 'number') {
            return meta.totalPages * pageSize;
        }

        if (meta && typeof meta.page === 'number') {
            const page = meta.page;
            const size = typeof meta.per_page === 'number' ? meta.per_page : pageSize;
            return (page + 1) * size;
        }

        return offset + currentLength;
    }

    public resolveHasNext(currentLength: number, pageSize: number): boolean {
        return currentLength === pageSize;
    }

    public buildQueryParams(params: IGerenciamentoDocumentosLoadParams): Record<string, any> {
        const query: Record<string, any> = {};

        if (params.status) {
            query.status = params.status;
        }

        if (params.tipoarquivo) {
            query.tipoarquivo = params.tipoarquivo;
        }

        if (params.trabalhador) {
            query.trabalhador = params.trabalhador;
        }

        if (params.competenciaano) {
            query.competenciaano = params.competenciaano;
        }

        if (params.competenciames) {
            query.competenciames = params.competenciames;
        }

        if (params.campos_customizados) {
            const camposCustomizados = typeof params.campos_customizados === 'string'
                ? params.campos_customizados
                : JSON.stringify(params.campos_customizados);

            if (camposCustomizados !== '{}' && camposCustomizados !== '') {
                query.campos_customizados = camposCustomizados;
            }
        }

        query.estabelecimento = '';

        if (params['trabalhador.empresa']) {
            query['trabalhador.empresa'] = params['trabalhador.empresa'];
        }

        if (params['trabalhador.estabelecimento']) {
            query['trabalhador.estabelecimento'] = params['trabalhador.estabelecimento'];
        }

        if (params['trabalhador.lotacao']) {
            query['trabalhador.lotacao'] = params['trabalhador.lotacao'];
        }

        if (params.grupo) {
            query.grupo = params.grupo;
        }

        if (params.order) {
            query.order = params.order;
        }

        if (params.offset !== undefined && params.offset !== null) {
            query.offset = params.offset;
        }

        if (params.limit !== undefined && params.limit !== null) {
            query.limit = params.limit;
        }

        return query;
    }

    private resolveOffsetValue(entity: IGerenciamentoDocumento | undefined): string | number | null {
        if (!entity) {
            return null;
        }

        return entity.arquivotrabalhador || null;
    }
}
